import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Layers,
  Sparkles,
  FileText,
  Image as ImageIcon,
  ChevronRight,
  Eye,
  Home,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { OutlineButton, PrimaryButton, CloseButton } from '../../components/Buttons';
import axiosInstance from '../../services/api/axios';
import { fetchProjectPhases, fetchProjectPhaseMedia, fetchPropertyConfigurations } from '../../services/projects';
import { buildPhaseDataItemFromProjectPhase } from '../../utils/buildInteriorPhasePayload';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import { getMediaUrl, mapMediaItems, normalizePropertyConfigMediaForUI } from '../../utils/media';
import { getCachedConfigMediaArray, setCachedConfigMediaArray } from '../../utils/configMediaSessionCache';
import DeveloperProjectBoqTable from '../../components/DeveloperProjectBoqTable';
import { PhaseMediaFileRow } from '../../components/PhaseDocumentFileRow';
import { organizePhaseDocumentsFromMedia } from '../../utils/organizePhaseDocumentsFromMedia';
import LoadingImage from '../../components/LoadingImage';

// Tabs available on this page. Hash-driven so the URL reflects the active section.
const TAB_IDS = ['overview', 'configs', 'documents', 'boq'];
const parseTabFromHash = (hash) => {
  if (!hash) return 'overview';
  const cleaned = hash.replace(/^#/, '').toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : 'overview';
};

/**
 * BV user phase details: same data as interior designer view without phase subscription / unlock flow.
 * All phase documents and configurations are shown by default.
 */
const BV_PhaseDetails = () => {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const navLocation = useLocation();
  const { userInfo } = useUser();

  // Get phase data from navigation state if available
  const passedPhaseData = navLocation.state?.phaseData;
  const [phase, setPhase] = useState(null);
  const [project, setProject] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseImage, setPhaseImage] = useState(null);
  const [configImages, setConfigImages] = useState({});
  const [phaseCompletionPercent, setPhaseCompletionPercent] = useState(0);
  const [buildings, setBuildings] = useState([]);
  // /projects/buildings returns building basics; per-building completion %
  // lives on /building/metadata. Fetched separately and averaged as the
  // ultimate fallback for phase completion when no rolled-up % is provided.
  const [buildingMetadata, setBuildingMetadata] = useState([]);
  const [propertyCounts, setPropertyCounts] = useState({});
  const [phaseMedia, setPhaseMedia] = useState({ images: [], guidelines: [], boq: [] });
  const [boqModalOpen, setBoqModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== 'undefined' ? parseTabFromHash(window.location.hash) : 'overview'
  );

  /** Full `/property/config/media` payload per config — passed to config details to avoid a duplicate media request */
  const configFullMediaByIdRef = useRef({});

  // Keep activeTab in sync with the URL hash (back/forward, deep links).
  useEffect(() => {
    setActiveTab(parseTabFromHash(navLocation.hash));
  }, [navLocation.hash]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      navigate(`${navLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  // Image caching utilities
  const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes

  const getCachedImage = (key) => {
    const cached = localStorage.getItem(`media_${key}`);
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      if (!parsed || !parsed.data || !parsed.cachedAt) return null;
      const isStale = Date.now() - parsed.cachedAt > CACHE_TTL_MS;
      return isStale ? null : parsed.data;
    } catch (error) {
      console.error("error while parsing cached data:", error);
      return null;
    }
  };

  const setCachedImage = (key, mediaData) => {
    try {
      const payload = {
        data: mediaData,
        cachedAt: Date.now(),
      };
      localStorage.setItem(`media_${key}`, JSON.stringify(payload));
    } catch (error) {
      console.error("Error storing in cache:", error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPhaseDetails = async () => {
      try {
        setLoading(true);

        // Step 1: Use passed phase data if available, otherwise fetch
        let phaseDataItem = passedPhaseData;

        if (!phaseDataItem) {
          try {
            if (!phaseId) {
              toast.error('Missing phase');
              navigate('/bv-user/phases');
              return;
            }

            const customerIdForPhase =
              userInfo?.loginIdentifierDetails?.customer?.customerId ??
              userInfo?.loginIdentifierDetails?.customerId ??
              userInfo?.customerId ??
              null;
            const phaseFetchParams = { projectPhaseId: phaseId };
            if (customerIdForPhase != null && customerIdForPhase !== '') {
              phaseFetchParams.customerId = customerIdForPhase;
            }
            const phaseResponse = await fetchProjectPhases(phaseFetchParams);
            let phasesArray = [];
            if (phaseResponse?.items && Array.isArray(phaseResponse.items)) {
              phasesArray = phaseResponse.items;
            } else if (Array.isArray(phaseResponse)) {
              phasesArray = phaseResponse;
            }
            const phaseIdNum = Number(phaseId);
            const phaseRow =
              phasesArray.find(
                (p) =>
                  Number(p.projectPhaseId) === phaseIdNum ||
                  Number(p.id) === phaseIdNum ||
                  Number(p.projectPhase?.projectPhaseId) === phaseIdNum
              ) || phasesArray[0];

            if (phaseRow) {
              let projectData = phaseRow.project || phaseRow.projectPhase?.project || null;
              const projectIdFromPhase =
                phaseRow.projectId ??
                phaseRow.projectPhase?.projectId ??
                projectData?.projectId ??
                null;

              if (!projectData && projectIdFromPhase) {
                const projectResponse = await axiosInstance.get(`/projects`, {
                  params: { projectId: projectIdFromPhase, customerId: null },
                });
                const items =
                  projectResponse?.items ||
                  (Array.isArray(projectResponse) ? projectResponse : []);
                projectData = items[0] || null;
              }

              phaseDataItem = buildPhaseDataItemFromProjectPhase(phaseRow, projectData);
            }
          } catch (fetchError) {
            console.error('Error fetching phase data:', fetchError);
            toast.error('Failed to load phase details');
            navigate('/bv-user/phases');
            return;
          }
        }

        if (!phaseDataItem || !phaseDataItem.projectPhase) {
          // Only show error and navigate if component is still mounted
          if (isMounted && !passedPhaseData) {
            console.warn('Phase not found in response');
            toast.error('Phase not found');
            navigate('/bv-user/phases');
          }
          return;
        }

        // Don't proceed if component unmounted
        if (!isMounted) return;

        // Extract all data directly from phase data (either passed or fetched)
        const projectPhase = phaseDataItem.projectPhase || {};
        const projectData = projectPhase.project || {};
        let buildingsArray = phaseDataItem.buildings || [];
        let configsArray =
          phaseDataItem.configs ||
          phaseDataItem.configurations ||
          phaseDataItem.propertyConfigurations ||
          [];
        const completionPercent = phaseDataItem.completionPercentage ?? 0;

        const resolvedProjectId =
          projectData?.projectId ?? projectPhase?.projectId ?? null;

        const resolvedCustomerId =
          userInfo?.loginIdentifierDetails?.customer?.customerId ??
          userInfo?.loginIdentifierDetails?.customerId ??
          userInfo?.customerId ??
          projectData?.customerId;

        // Phase payload often omits buildings; GET /projects/buildings?projectPhaseId=&customerId=
        if ((!buildingsArray || buildingsArray.length === 0) && phaseId && resolvedProjectId) {
          try {
            const bParams = { projectPhaseId: phaseId };
            if (resolvedCustomerId != null && resolvedCustomerId !== '') {
              bParams.customerId = resolvedCustomerId;
            }
            const bResp = await axiosInstance.get('/projects/buildings', { params: bParams });
            let bList = [];
            if (bResp?.items && Array.isArray(bResp.items)) bList = bResp.items;
            else if (Array.isArray(bResp)) bList = bResp;
            if (bList.length > 0) buildingsArray = bList;
          } catch (bErr) {
            console.error('Error fetching buildings for phase:', bErr);
          }
        }

        // /projects/phase often omits nested configs; load them like BuildingsAndConfigs does
        if ((!configsArray || configsArray.length === 0) && resolvedProjectId && phaseId) {
          try {
            const params = {
              projectId: resolvedProjectId,
              projectPhaseId: phaseId,
            };
            if (resolvedCustomerId != null && resolvedCustomerId !== '') {
              params.customerId = resolvedCustomerId;
            }
            const configResponse = await fetchPropertyConfigurations(params);
            let list = [];
            if (configResponse?.items && Array.isArray(configResponse.items)) {
              list = configResponse.items;
            } else if (Array.isArray(configResponse)) {
              list = configResponse;
            }
            configsArray = list;
          } catch (cfgErr) {
            console.error('Error fetching property configurations for phase:', cfgErr);
          }
        }

        // Set all data from phase data
        setPhase(projectPhase);
        setProject(projectData);
        setBuildings(buildingsArray);
        setPhaseCompletionPercent(completionPercent);
        setConfigurations(configsArray);

        // Calculate property counts directly from configs (using propertiesId array)
        const counts = {};
        configsArray.forEach((config) => {
          const configId = config.propertyConfigId || config.id;
          // Use propertiesId array length from config data (or 0 if null)
          counts[configId] = Array.isArray(config.propertiesId) ? config.propertiesId.length : 0;
        });
        setPropertyCounts(counts);

        // Step 2: Fetch phase image (only if not cached and not in response)
        const cacheKey = `phase_${phaseId}`;
        const cachedPhaseImage = getCachedImage(cacheKey);

        if (cachedPhaseImage) {
          setPhaseImage(cachedPhaseImage);
        } else {
          // Fetch phase image if not cached
          try {
            const phaseImageResponse = await axiosInstance.get('/projects/phase/media', {
              params: { projectPhaseId: phaseId, images: true, progress: false, guidelines: false, boq: false }
            }).catch(() => ({ items: [], data: [] }));

            let imagesArray = [];
            if (phaseImageResponse?.items && Array.isArray(phaseImageResponse.items)) {
              imagesArray = phaseImageResponse.items;
            } else if (Array.isArray(phaseImageResponse)) {
              imagesArray = phaseImageResponse;
            }

            if (imagesArray.length > 0) {
              const [mediaData] = mapMediaItems(imagesArray, 'Phase Image');
              setPhaseImage(mediaData);
              if (mediaData) {
                setCachedImage(cacheKey, mediaData);
              }
            }
          } catch (error) {
            console.error('Error processing phase images:', error);
          }
        }

        // Step 3: Fetch config images only (property counts already available from configs)
        if (configsArray.length > 0) {
          // Check cache for config images first
          const imagesMap = {};
          const configsToFetch = [];

          configsArray.forEach((config) => {
            const configId = config.propertyConfigId || config.id;
            const cacheKey = `config_${configId}`;
            const cachedConfigImage = getCachedImage(cacheKey);
            if (cachedConfigImage) {
              imagesMap[configId] = cachedConfigImage;
            } else {
              configsToFetch.push(config);
            }
          });

          // Fetch config images in parallel (only for non-cached ones)
          if (configsToFetch.length > 0) {
            const configImagePromises = configsToFetch.map(async (config) => {
              const configId = config.propertyConfigId || config.id;
              const cacheKey = `config_${configId}`;

              try {
                const cid = String(configId);
                let rawMediaPayload;
                const sessionCached = getCachedConfigMediaArray(configId);
                if (sessionCached !== undefined && sessionCached !== null) {
                  rawMediaPayload = sessionCached;
                } else {
                  const mediaParams = { property_config_id: configId };
                  if (resolvedCustomerId != null && resolvedCustomerId !== '') {
                    mediaParams.customerId = resolvedCustomerId;
                  }
                  const configImagesResponse = await axiosInstance.get('/property/config/media', {
                    params: mediaParams,
                  });
                  rawMediaPayload = configImagesResponse;
                  setCachedConfigMediaArray(configId, rawMediaPayload);
                }

                configFullMediaByIdRef.current[cid] = rawMediaPayload;

                const { gallery } = normalizePropertyConfigMediaForUI(rawMediaPayload);
                const mediaData = gallery[0] || null;
                if (mediaData) {
                  setCachedImage(cacheKey, mediaData);
                  return { configId, image: mediaData };
                }
              } catch (error) {
                console.error(`Error fetching images for config ${configId}:`, error);
              }
              return { configId, image: null };
            });

            const imageResults = await Promise.all(configImagePromises);

            // Merge fetched images with cached ones
            imageResults.forEach(({ configId, image }) => {
              if (image) {
                imagesMap[configId] = image;
              }
            });
          }

          setConfigImages(imagesMap);
        }

        // Phase documents: guidelines + RERA/BOQ file uploads
        try {
          const mediaData = await fetchProjectPhaseMedia(phaseId, {
            images: false,
            progress: false,
            guidelines: true,
            boq: true,
          });
          const mediaArray = Array.isArray(mediaData) ? mediaData : mediaData?.items || [];
          const organized = organizePhaseDocumentsFromMedia(mediaArray);
          if (isMounted) setPhaseMedia(organized);
        } catch (docErr) {
          console.error('Error fetching phase documents:', docErr);
          if (isMounted) setPhaseMedia({ images: [], guidelines: [], boq: [] });
        }

      } catch (error) {
        console.error('Error fetching phase details:', error);
        if (isMounted) {
          toast.error('Failed to load phase details');
          navigate('/bv-user/phases');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (phaseId) {
      fetchPhaseDetails();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseId, userInfo]); // userInfo: customerId for /property/config when available

  // Building metadata — the field `progressPercentCompletion` actually lives
  // here, not on `/projects/buildings`. We fetch and stash it so the
  // completion-percent fallback chain has something to average.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!phaseId) return;
      try {
        const resp = await axiosInstance.get('/building/metadata', {
          params: { projectPhaseId: phaseId },
        });
        let list = [];
        if (resp?.items && Array.isArray(resp.items)) list = resp.items;
        else if (Array.isArray(resp)) list = resp;
        if (!cancelled) setBuildingMetadata(list);
      } catch (err) {
        console.error('Error fetching building metadata for phaseId', phaseId, err);
        if (!cancelled) setBuildingMetadata([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [phaseId]);

  useEffect(() => {
    if (!boqModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setBoqModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [boqModalOpen]);

  const handleBackClick = () => {
    navigate('/bv-user/phases');
  };

  const handleConfigClick = (config) => {
    const configId = config.propertyConfigId || config.id;
    const pid = project?.projectId;
    if (pid && configId) {
      const cid = String(configId);
      const navState = {
        configData: config,
        projectData: project,
        phaseId: phaseId,
        buildings,
      };
      if (configFullMediaByIdRef.current[cid] !== undefined) {
        navState.prefetchedConfigMedia = configFullMediaByIdRef.current[cid];
      }
      navigate(`/bv-user/phases/${pid}/configs/${configId}`, { state: navState });
    }
  };

  // Clean skeleton loader using the slate visual language.
  const SkeletonLoader = () => (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-12 w-72 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!phase || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Building2 className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Phase not found</p>
          <p className="mt-1 text-sm text-slate-500">The phase you&rsquo;re looking for is unavailable.</p>
          <OutlineButton onClick={handleBackClick} className="mt-4">
            Back to Phases
          </OutlineButton>
        </div>
      </div>
    );
  }

  // Completion percentage — try, in order:
  // 1. Server-supplied phaseDataItem.completionPercentage
  // 2. phase.progressPercentCompletion (older shape)
  // 3. Average of buildings' progressPercentCompletion (the underlying status
  //    data the user expects to drive this number when the rolled-up field
  //    isn't populated by the BE yet)
  // Average across /building/metadata entries first; fall back to the
  // /projects/buildings list shape if metadata didn't return anything.
  const buildingsAvgCompletion = (() => {
    const sources = [
      Array.isArray(buildingMetadata) ? buildingMetadata : [],
      Array.isArray(buildings) ? buildings : [],
    ];
    for (const src of sources) {
      const vals = src
        .map((b) => parseFloat(b?.progressPercentCompletion))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (vals.length > 0) {
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      }
    }
    return 0;
  })();
  const completionPercent = (() => {
    const fromServer = parseFloat(phaseCompletionPercent);
    if (Number.isFinite(fromServer) && fromServer > 0) return fromServer;
    const fromPhase = parseFloat(phase.progressPercentCompletion);
    if (Number.isFinite(fromPhase) && fromPhase > 0) return fromPhase;
    return buildingsAvgCompletion;
  })();
  const cityState = project.address
    ? `${project.address.city || ''}, ${project.address.state || ''}`.replace(/^,\s*|,\s*$/g, '')
    : 'Location not available';
  const fullAddress = (() => {
    if (!project.address) return cityState;
    const a = project.address;
    const parts = [
      [a.buildingNo, a.streetName1].filter(Boolean).join(' '),
      a.streetName2,
      a.district,
      a.city,
      a.state,
      a.pinCode,
    ].filter((s) => s && String(s).trim() !== '');
    return parts.length ? parts.join(', ') : cityState;
  })();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Stat tiles shown in Overview.
  const statTiles = [
    {
      label: 'Configurations',
      value: configurations.length,
      icon: Layers,
    },
    {
      label: 'Buildings',
      value: buildings.length,
      icon: Building2,
    },
    {
      label: 'Total properties',
      value: Object.values(propertyCounts).reduce((sum, n) => sum + (n || 0), 0),
      icon: Home,
    },
    {
      label: 'Towers',
      value:
        phase.towers !== null && phase.towers !== undefined && phase.towers !== ''
          ? phase.towers
          : '—',
      icon: ShieldCheck,
    },
  ];

  // Tab metadata — drives the pill nav.
  const TABS = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'configs', label: 'Configurations', icon: Layers, count: configurations.length },
    {
      id: 'documents',
      label: 'Documents',
      icon: FileText,
      count: phaseMedia.guidelines.length + phaseMedia.boq.length,
    },
    { id: 'boq', label: 'Developer BOQ', icon: ImageIcon },
  ];

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Back link above the hero */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to phases
      </button>

      {/* Hero band — slate gradient with radial overlay, status row + completion pill */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
          }}
        />
        {phaseImage && (
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <LoadingImage
              src={getMediaUrl(phaseImage)}
              alt={phase.name || 'Phase'}
              containerClassName="w-full h-full"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <Sparkles size={12} />
            Buildvizer User &middot; Phase
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {phase.name || 'Unnamed Phase'}
          </h1>

          {(project.projectName || project.developer) && (
            <p className="mt-1 text-sm text-white/80">
              {project.projectName || project.developer}
              {project.projectName && project.developer ? (
                <span className="text-white/60"> &middot; {project.developer}</span>
              ) : null}
            </p>
          )}

          <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
            <MapPin size={13} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{fullAddress}</span>
          </div>

          {/* Status row: completion pill + RERA chip */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
              {completionPercent.toFixed(2)}% complete
            </span>
            {phase.reraNo && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                RERA &middot; {phase.reraNo}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Pill tab nav */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {TABS.map(({ id, label, icon: Icon, count }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {label}
              {typeof count === 'number' && count > 0 && (
                <span
                  className={`tabular-nums rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Quick snapshot of what this phase contains.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {statTiles.map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About + completion */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900">About the project</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {project.developer ? `${project.developer} · ` : ''}
                {phase.name || 'This phase'}
              </p>

              {project.description?.trim() ? (
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {project.description.trim()}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-slate-400">No description provided.</p>
              )}

              <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Location
                  </p>
                  <p className="mt-1 text-sm text-slate-900">{cityState}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Project type
                  </p>
                  <p className="mt-1 text-sm text-slate-900">
                    {project.type != null && String(project.type).trim() !== '' ? project.type : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Phase completion</h2>
              <p className="mt-0.5 text-sm text-slate-500">Overall progress for this phase</p>

              <div className="mt-5 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 transition-[width] duration-300"
                      style={{ width: `${Math.min(100, Math.max(0, completionPercent))}%` }}
                    />
                  </div>
                </div>
                <span className="shrink-0 text-2xl font-semibold text-slate-900 tabular-nums">
                  {completionPercent.toFixed(2)}%
                </span>
              </div>

              <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Unit configurations</span>
                  <span className="font-medium text-slate-900">{configurations.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Buildings</span>
                  <span className="font-medium text-slate-900">{buildings.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Total properties</span>
                  <span className="font-medium text-slate-900">
                    {Object.values(propertyCounts).reduce((sum, n) => sum + (n || 0), 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RERA & inventory */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">RERA &amp; inventory</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Regulatory registration and unit summary.
            </p>
            <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  RERA registration
                </dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.reraNo != null && String(phase.reraNo).trim() !== ''
                    ? String(phase.reraNo).trim()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Towers</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.towers !== null && phase.towers !== undefined && phase.towers !== ''
                    ? phase.towers
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Units</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.units !== null && phase.units !== undefined && phase.units !== ''
                    ? phase.units
                    : '—'}
                </dd>
              </div>
              {(phase.reraStartDate || phase.reraEndDate) && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    RERA period
                  </dt>
                  <dd className="mt-1 font-medium text-slate-900">
                    {phase.reraStartDate ? formatDate(phase.reraStartDate) : '—'}
                    {' '}
                    <span className="text-slate-400">–</span>
                    {' '}
                    {phase.reraEndDate ? formatDate(phase.reraEndDate) : '—'}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Buildings */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Buildings</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {buildings.length === 0
                ? 'No buildings listed for this phase.'
                : `${buildings.length} ${buildings.length === 1 ? 'building' : 'buildings'} in this phase`}
            </p>

            {buildings.length === 0 ? (
              <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-6 text-center">
                <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <Building2 className="text-slate-400" size={18} />
                </div>
                <p className="mt-2 text-sm text-slate-500">None listed for this phase.</p>
              </div>
            ) : (
              <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {buildings.map((building) => {
                  const buildingId = building.buildingId || building.id;
                  const buildingName = building.buildingName || building.name || 'Unnamed building';
                  const floors = building.numberOfFloors ?? building.floors;
                  const floorText =
                    floors != null && floors !== ''
                      ? `${floors} ${Number(floors) === 1 ? 'floor' : 'floors'}`
                      : null;
                  return (
                    <li
                      key={buildingId ?? buildingName}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/40 p-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200">
                        <Building2 size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {buildingName}
                        </p>
                        {floorText && (
                          <p className="text-xs text-slate-500">{floorText}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'configs' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Available configurations</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Floor plans and unit layouts available in this phase.
              </p>
            </div>
            {configurations.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Layers size={12} />
                {configurations.length} {configurations.length === 1 ? 'config' : 'configs'}
              </span>
            )}
          </div>

          {configurations.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Building2 className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No configurations available</p>
              <p className="mt-1 text-sm text-slate-500">
                Configurations will appear here when added to this phase.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {configurations.map((config) => {
                const configId = config.propertyConfigId || config.id;
                const configImage = configImages[configId];
                const configName = config.configName || config.propertyConfig?.configName || 'Unnamed Config';
                const propertyCount = propertyCounts[configId] !== undefined ? propertyCounts[configId] : 0;
                const fileCount = config.images?.length || (configImage ? 1 : 0);

                return (
                  <div
                    key={configId}
                    onClick={() => handleConfigClick(config)}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="relative overflow-hidden">
                      {configImage ? (
                        <LoadingImage
                          src={getMediaUrl(configImage)}
                          alt={configName}
                          containerClassName="h-40 w-full"
                          className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <Building2 className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="truncate text-base font-semibold text-slate-900" title={configName}>
                        {configName}
                      </h3>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                          {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
                        </span>
                        {fileCount > 0 && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                            {fileCount} {fileCount === 1 ? 'file' : 'files'}
                          </span>
                        )}
                      </div>

                      <div className="mt-auto pt-4">
                        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                          <span className="text-xs font-medium text-slate-500">View configuration</span>
                          <ChevronRight
                            size={14}
                            className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-flamingo-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Guidelines */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Interior design guidelines</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {phaseMedia.guidelines.length === 0
                    ? 'No guideline files uploaded yet.'
                    : `${phaseMedia.guidelines.length} ${phaseMedia.guidelines.length === 1 ? 'file' : 'files'}`}
                </p>
              </div>
              {phaseMedia.guidelines.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  <FileText size={12} />
                  {phaseMedia.guidelines.length}
                </span>
              )}
            </div>

            {phaseMedia.guidelines.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <FileText className="text-slate-400" size={22} />
                </div>
                <p className="mt-3 text-sm text-slate-500">No guideline files uploaded.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {phaseMedia.guidelines.map((doc, docIndex) => (
                  <PhaseMediaFileRow
                    key={doc.key || doc.id || doc.mediaId || `g-${docIndex}`}
                    doc={doc}
                    docIndex={docIndex}
                    defaultLabel="Guideline"
                    compact
                  />
                ))}
              </ul>
            )}
          </div>

          {/* RERA BOQ docs */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">RERA BOQ documents</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {phaseMedia.boq.length === 0
                    ? 'No BOQ files uploaded yet.'
                    : `${phaseMedia.boq.length} ${phaseMedia.boq.length === 1 ? 'file' : 'files'}`}
                </p>
              </div>
              {phaseMedia.boq.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  <FileText size={12} />
                  {phaseMedia.boq.length}
                </span>
              )}
            </div>

            {phaseMedia.boq.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <FileText className="text-slate-400" size={22} />
                </div>
                <p className="mt-3 text-sm text-slate-500">No BOQ documents uploaded.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {phaseMedia.boq.map((doc, docIndex) => (
                  <PhaseMediaFileRow
                    key={doc.key || doc.id || doc.mediaId || `b-${docIndex}`}
                    doc={doc}
                    docIndex={docIndex}
                    defaultLabel="RERA BOQ"
                    rightAction="view"
                    compact
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'boq' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Developer project BOQ</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                View the developer&rsquo;s Bill of Quantities for this phase.
              </p>
            </div>
            <PrimaryButton
              type="button"
              onClick={() => setBoqModalOpen(true)}
              className="!w-auto !px-4 !py-2 text-sm whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <Eye size={14} />
                Open BOQ
              </span>
            </PrimaryButton>
          </div>

          <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50/40 p-4">
            <p className="text-sm text-slate-600">
              Click <span className="font-medium text-slate-900">Open BOQ</span> above to view the
              developer&rsquo;s Bill of Quantities in a dedicated viewer.
            </p>
          </div>
        </div>
      )}

      {/* Developer BOQ modal (unchanged behavior) */}
      {boqModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bv-boq-modal-title"
          onClick={() => setBoqModalOpen(false)}
        >
          <div
            className="flex w-full max-w-5xl max-h-[90vh] flex-col rounded-2xl border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h3 id="bv-boq-modal-title" className="text-lg font-semibold text-slate-900">
                  Developer project BOQ
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {phase.name || 'This phase'}
                  {project.projectName ? ` · ${project.projectName}` : ''}
                </p>
              </div>
              <CloseButton
                type="button"
                onClick={() => setBoqModalOpen(false)}
                aria-label="Close dialog"
                className="shrink-0 border-slate-300"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-6">
              <DeveloperProjectBoqTable
                projectPhaseId={phaseId}
                readOnly
                showTitle={false}
                title=""
                className="p-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BV_PhaseDetails;
